import { chain, omit, flatMap } from 'lodash'
import { CFEvent, CFOffer, CFCentre } from '../../../types/contentful'
import {
  MDate,
  WhatsOnRange,
  WhatsOnEvent,
  WhatsOnOffer,
  WhatsOnEventOccurrence,
  WhatsOnOfferOccurrence,
  WhatsOnOccurrence,
  TimeRange,
  WhatsOnSelectedFilters,
} from '../../../types/whats-on'
import { rangesForDate, isInTimeRange } from '../../helpers/time-ranges'

import doesMatchMultiFilter from '../../helpers/does-match-multi-filter'
import {
  cfEventToEventOccurrences,
  cfOfferToOfferOccurrences,
} from './contentful-to-whats-on'

type WhatsOnAnyOccurrence = WhatsOnEventOccurrence | WhatsOnOfferOccurrence

function doesMatchWhatsOnMultiFilters(
  selectedFilters: WhatsOnSelectedFilters
): (occurrence: WhatsOnAnyOccurrence) => boolean {
  const get = (
    occurrence: WhatsOnAnyOccurrence,
    key: Exclude<keyof WhatsOnSelectedFilters, 'dateRange'>
  ): string[] => occurrence.item[key]
  return doesMatchMultiFilter(omit(selectedFilters, 'dateRange'), get)
}

type WhatsOnFromCF<CFItem extends CFOffer | CFEvent> = CFItem extends CFOffer
  ? WhatsOnOffer
  : WhatsOnEvent

type ToOccurrences<CFItem extends CFOffer | CFEvent> = (
  baseDate: MDate,
  centre: CFCentre,
  entry: CFItem
) => WhatsOnOccurrence<WhatsOnFromCF<CFItem>>[]

interface CoreRangeParams {
  baseDate: MDate
  centre: CFCentre
  timeRange: TimeRange
  selectedFilters: WhatsOnSelectedFilters
}

function cfEntriesToOccurrences<CFItem extends CFEvent | CFOffer>(
  baseDate: MDate,
  centre: CFCentre,
  entries: CFItem[],
  toOccurrences: ToOccurrences<CFItem>
): WhatsOnOccurrence<WhatsOnFromCF<CFItem>>[] {
  return flatMap(entries, entry => toOccurrences(baseDate, centre, entry))
}

function applyFiltersToCfOccurrences<
  WhatsOnOccurrence extends WhatsOnOfferOccurrence | WhatsOnEventOccurrence
>(
  selectedFilters: WhatsOnSelectedFilters,
  occurrences: WhatsOnOccurrence[]
): WhatsOnOccurrence[] {
  return occurrences.filter(doesMatchWhatsOnMultiFilters(selectedFilters))
}

function getOccurrencesByTimeRange<CFItem extends CFEvent | CFOffer>(
  timeRange: TimeRange,
  filteredOccurrences: WhatsOnOccurrence<WhatsOnFromCF<CFItem>>[],
  orderArgs: [string[], ('asc' | 'desc')[]]
): WhatsOnFromCF<CFItem>[] {
  return chain(filteredOccurrences)
    .filter(isInTimeRange(timeRange))
    .uniqBy('item.slug')
    .orderBy(...orderArgs)
    .map('item')
    .value()
}

interface OccurrencesLists {
  offersOccurrences: WhatsOnOfferOccurrence[]
  eventsOccurrences: WhatsOnEventOccurrence[]
}

export const getOcccurrencesLists = (
  baseDate: MDate,
  events: CFEvent[],
  offers: CFOffer[],
  centre: CFCentre
): OccurrencesLists => {
  const eventsOccurrences = cfEntriesToOccurrences(
    baseDate,
    centre,
    events,
    cfEventToEventOccurrences
  )
  const offersOccurrences = cfEntriesToOccurrences(
    baseDate,
    centre,
    offers,
    cfOfferToOfferOccurrences
  )
  return { eventsOccurrences, offersOccurrences }
}

export const rangesFromOccurrencesListsAndFilters = (
  baseDate: MDate,
  occurrencesLists: OccurrencesLists,
  selectedFilters: WhatsOnSelectedFilters
): WhatsOnRange[] => {
  const { eventsOccurrences, offersOccurrences } = occurrencesLists
  const filteredEvents = applyFiltersToCfOccurrences(
    selectedFilters,
    eventsOccurrences
  )
  const filteredOffers = applyFiltersToCfOccurrences(
    selectedFilters,
    offersOccurrences
  )

  return rangesForDate(baseDate, selectedFilters.dateRange).map(timeRange => {
    const whatsOnEvents = getOccurrencesByTimeRange(timeRange, filteredEvents, [
      ['startAt', 'item.name'],
      ['asc', 'asc'],
    ])
    const whatsOnOffers = getOccurrencesByTimeRange(timeRange, filteredOffers, [
      ['item.timezonelessShowAtTimestamp', 'item.name'],
      ['desc', 'asc'],
    ])
    return {
      name: timeRange.name,
      slug: timeRange.slug(),
      items: [...whatsOnEvents, ...whatsOnOffers],
    }
  })
}
